import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  MenuItem,
  Chip
} from "@mui/material";
import { SiMicrosoftexcel } from 'react-icons/si';
import { BsFilePdfFill } from 'react-icons/bs';
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import CustomDateRange from "../../Components/CustomDateRange";
import transactionAPI from "../../Network/Transaction";
import transactionStatusAPI from "../../Network/TransactionStatus";
import unitAPI from "../../Network/Unit";
import mizanListAPI from "../../Network/MizanList";
import geofenceAPI from "../../Network/Geofence";
import userAPI from "../../Network/User";
import municipalityAPI from "../../Network/Municipality";
import mizanUnitAPI from "../../Network/MizanUnit";
import Title from "./Title";
import Grid from "../Grid/Grid";
import useFilterVisibility from "../PrivateFilters/useFilterVisibility";

const cookies = new Cookies();

export default function ReportUnitTransactionSummary() {
  const [t] = useTranslation("common");
  const navigate = useNavigate();
  // Per-filter public/private visibility for this page. <Gate name="key"> hides
  // a filter when it's marked private and the user lacks show_private_filters.
  const { isVisible } = useFilterVisibility("report_unit_transaction_summary");
  // Memoized: a fresh Gate each render would remount wrapped filters every
  // keystroke (clearing search inputs). isVisible is stable (useCallback).
  const Gate = React.useMemo(
    () => ({ name, children }) => (isVisible(name) ? children : null),
    [isVisible]
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    unit_id: null,
    unit: null,
    search_account_id: null,
    account: null,
    municipality_id: null,
    municipality: null,
    mizan_id: null,
    mizan_selected: null,
    source_geofence_id: null,
    sourceGeofence: null,
    destination_geofence_id: null,
    destinationGeofence: null,
    creationDateFrom: moment().startOf('month').toDate(),
    creationDateTo: moment().endOf('day').toDate(),
    creation_date_from: moment().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
    creation_date_to: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    is_governmental: null,
    transaction_status_ids: [],
    transaction_status_selected: [],
    groups_selected: [],
    group_ids: [],
    unit_type_id: null,
    unit_type_selected: null,
    all_time: 0,
    export_excel: false,
    export_pdf: false
  });

  const [reload, setReload] = useState(false);

  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [municipalities, setMunicipalities] = useState([]);
  const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);
  const [mizans, setMizans] = useState([]);
  const [loadingMizans, setLoadingMizans] = useState(false);
  const [sourceGeofences, setSourceGeofences] = useState([]);
  const [loadingSourceGeofences, setLoadingSourceGeofences] = useState(false);
  const [destinationGeofences, setDestinationGeofences] = useState([]);
  const [loadingDestinationGeofences, setLoadingDestinationGeofences] = useState(false);
  const [transactionStatuses, setTransactionStatuses] = useState([]);
  const [loadingTransactionStatuses, setLoadingTransactionStatuses] = useState(false);
  const [unitTypes, setUnitTypes] = useState([]);
  const [loadingUnitTypes, setLoadingUnitTypes] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const timerAccounts = useRef(null);
  const timerSourceGeofences = useRef(null);
  const timerDestinationGeofences = useRef(null);
  // Filters change after gridColumns is initialized via useState, so renderCell
  // closures keep referencing the initial filters. The ref gives handleRedirect
  // a way to read the latest filters at click time.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const [pageSize, setPageSize] = useState(100);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const timerUnits = useRef(null);

  const [totals, setTotals] = useState({
    total_transactions: 0,
    total_not_audited: 0,
    total_audited_valid: 0,
    total_audited_invalid: 0,
    total_weight: 0,
    total_weight_not_audited: 0,
    total_weight_valid: 0,
    total_weight_invalid: 0
  });

  const headerObject = {
    username: cookies.get("user_name") ? cookies.get("user_name") : null,
    user_id: cookies.get("user_id") ? cookies.get("user_id") : null,
    account_name: cookies.get("account_name") ? cookies.get("account_name") : null,
    account_id: cookies.get("account_id") ? cookies.get("account_id") : null,
    authHash: cookies.get("authHash") ? cookies.get("authHash") : null,
    sid: cookies.get("sid") ? cookies.get("sid") : null,
    baseurl: cookies.get("baseUrl") ? cookies.get("baseUrl") : null,
    id: cookies.get("id") ? cookies.get("id") : null,
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const isExcel = !!filters.export_excel;
      const isPdf = !!filters.export_pdf;
      const isExport = isExcel || isPdf;
      const requestBody = {
        unit_id: filters.unit_id,
        search_account_id: filters.search_account_id,
        municipality_id: filters.municipality_id,
        mizan_id: filters.mizan_id,
        source_geofence_id: filters.source_geofence_id,
        destination_geofence_id: filters.destination_geofence_id,
        creation_date_from: filters.creation_date_from,
        creation_date_to: filters.creation_date_to,
        is_governmental: filters.is_governmental,
        transaction_status_ids: filters.transaction_status_ids,
        group_ids: filters.group_ids,
        unit_type_id: filters.unit_type_id,
        ...(isExport
          ? {
              ...(isExcel ? { export_excel: true } : {}),
              ...(isPdf ? { export_pdf: true } : {}),
              applied_filters: buildAppliedFiltersForExport()
            }
          : { page: pageNumber, page_size: pageSize })
      };

      const response = await transactionAPI.getUnitTransactionsSummary({
        params: headerObject,
        data: requestBody
      });

      if (isExport) {
        // Reset the flags regardless of outcome so the next search runs in normal mode.
        setFilters(prev => ({ ...prev, export_excel: false, export_pdf: false }));
        if (response.status && response?.data?.status && response?.data?.data) {
          if (isExcel) {
            await downloadExcelFile(response.data.data);
          } else {
            await downloadPdfFile(response.data.data);
          }
        } else {
          toast.error(response?.data?.message || t("NETWORK_MESSAGES.unknownError"));
        }
      } else if (response.status && response?.data?.status) {
        const dataRows = response?.data?.data?.data || [];
        const backendTotals = response?.data?.data?.totals || {};
        const totalRecords = response?.data?.data?.total_records || 0;

        setRows(dataRows.map((row, index) => ({ ...row, id: index })));
        setRowsTotal(totalRecords);
        setPageCount(Math.ceil(totalRecords / pageSize));

        setTotals({
          total_transactions: Number(backendTotals.total_transactions || 0),
          total_not_audited: Number(backendTotals.total_not_audited_transactions || 0),
          total_audited_valid: Number(backendTotals.total_audited_and_valid_transactions || 0),
          total_audited_invalid: Number(backendTotals.total_audited_and_not_valid_transactions || 0),
          total_weight: Number(backendTotals.total_weights_of_transactions || 0),
          total_weight_not_audited: Number(backendTotals.total_weight_for_not_audited_transactions || 0),
          total_weight_valid: Number(backendTotals.total_weight_for_audited_and_valid_transactions || 0),
          total_weight_invalid: Number(backendTotals.total_weight_for_audited_and_not_valid_transactions || 0)
        });
      } else {
        toast.error(response?.data?.message || t("NETWORK_MESSAGES.unknownError"));
      }
    } catch (error) {
      toast.error(error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [reload, pageNumber, pageSize]);

  const handleSearchTermUnitChange = (newValue) => {
    if (timerUnits.current) clearTimeout(timerUnits.current);
    timerUnits.current = setTimeout(async () => {
      if (newValue?.length >= 3) {
        setLoadingUnits(true);
        const result = await unitAPI.getUnitList({
          params: { ...headerObject, name: newValue, page_size: 20, page: 1 }
        });
        setLoadingUnits(false);
        if (result.status && result?.data?.data?.data) {
          setUnits(result?.data?.data?.data);
        }
      } else {
        setUnits([]);
      }
    }, 1000);
  };

  const handleSearchTermAccountChange = (newValue) => {
    if (timerAccounts.current) clearTimeout(timerAccounts.current);
    timerAccounts.current = setTimeout(async () => {
      if (newValue?.length >= 3 || newValue === "") {
        setLoadingAccounts(true);
        const result = await userAPI.getAccounts({
          params: { ...headerObject, name: newValue, page_size: 100, page: 1 }
        });
        setLoadingAccounts(false);
        if (result.status && result?.data?.data?.data) {
          setAccounts(result?.data?.data?.data);
        }
      } else {
        setAccounts([]);
      }
    }, newValue === "" ? 0 : 1000);
  };

  const handleSearchTermSourceGeofenceChange = (newValue) => {
    if (timerSourceGeofences.current) clearTimeout(timerSourceGeofences.current);
    timerSourceGeofences.current = setTimeout(async () => {
      if (newValue?.length >= 3 || newValue === "") {
        setLoadingSourceGeofences(true);
        const result = await geofenceAPI.getGeofenceList({
          params: { ...headerObject, name: newValue, port_type_ids: [1, 3], page_size: 100, page: 1 }
        });
        setLoadingSourceGeofences(false);
        if (result.status && result?.data?.data?.data) {
          setSourceGeofences(result?.data?.data?.data);
        }
      } else {
        setSourceGeofences([]);
      }
    }, newValue === "" ? 0 : 1000);
  };

  const handleSearchTermDestinationGeofenceChange = (newValue) => {
    if (timerDestinationGeofences.current) clearTimeout(timerDestinationGeofences.current);
    timerDestinationGeofences.current = setTimeout(async () => {
      if (newValue?.length >= 3 || newValue === "") {
        setLoadingDestinationGeofences(true);
        const result = await geofenceAPI.getGeofenceList({
          params: { ...headerObject, name: newValue, port_type_ids: [2, 3], page_size: 100, page: 1 }
        });
        setLoadingDestinationGeofences(false);
        if (result.status && result?.data?.data?.data) {
          setDestinationGeofences(result?.data?.data?.data);
        }
      } else {
        setDestinationGeofences([]);
      }
    }, newValue === "" ? 0 : 1000);
  };

  const loadInitialLookups = async () => {
    setLoadingMunicipalities(true);
    setLoadingMizans(true);
    setLoadingUnitTypes(true);
    setLoadingGroups(true);
    try {
      const mResult = await municipalityAPI.getMunicipalityList({ params: headerObject });
      if (mResult.status && mResult?.data?.data) setMunicipalities(mResult?.data?.data);
      
      const mizResult = await mizanListAPI.getMizanLookup({ params: headerObject });
      if (mizResult.status && mizResult?.data?.data) setMizans(mizResult?.data?.data);

      const lookupsResult = await mizanUnitAPI.getLookups({ params: headerObject });
      if (lookupsResult.status && lookupsResult?.data?.data?.unit_types) {
        setUnitTypes(lookupsResult.data.data.unit_types);
      }

      const groupsResult = await transactionAPI.getUserUnitGroups({ params: headerObject });
      if (groupsResult.status && Array.isArray(groupsResult?.data?.data)) {
        setGroups(groupsResult?.data?.data);
      }

      handleSearchTermAccountChange("");
      handleSearchTermSourceGeofenceChange("");
      handleSearchTermDestinationGeofenceChange("");
    } catch (e) { console.error(e); }
    setLoadingMunicipalities(false);
    setLoadingMizans(false);
    setLoadingUnitTypes(false);
    setLoadingGroups(false);
  };

  useEffect(() => {
    loadInitialLookups();
    loadTransactionStatuses();
  }, []);

  const loadTransactionStatuses = async () => {
    setLoadingTransactionStatuses(true);
    try {
      const response = await transactionStatusAPI.getTransactionStatusList({
        params: headerObject
      });
      if (response.status && response?.data?.data) {
        setTransactionStatuses(response?.data?.data);
      }
    } catch (e) {
      console.error(e);
    }
    setLoadingTransactionStatuses(false);
  };

  const clearFilters = () => {
    setFilters({
      unit_id: null,
      unit: null,
      search_account_id: null,
      account: null,
      municipality_id: null,
      municipality: null,
      mizan_id: null,
      mizan_selected: null,
      source_geofence_id: null,
      sourceGeofence: null,
      destination_geofence_id: null,
      destinationGeofence: null,
      creationDateFrom: moment().startOf('month').toDate(),
      creationDateTo: moment().endOf('day').toDate(),
      creation_date_from: moment().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
      creation_date_to: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      is_governmental: null,
      transaction_status_ids: [],
      transaction_status_selected: [],
      groups_selected: [],
      group_ids: [],
      unit_type_id: null,
      unit_type_selected: null,
      all_time: 0,
      export_excel: false,
      export_pdf: false
    });
  };

  const buildAppliedFiltersForExport = () => {
    const out = [];
    if (filters.unit) out.push({ label: "المركبة", value: filters.unit.name || '' });
    if (filters.account) out.push({ label: "العائدية او الحساب", value: filters.account.name || '' });
    if (filters.is_governmental !== null && filters.is_governmental !== '') {
      out.push({ label: "نوع الجهد", value: String(filters.is_governmental) === '1' ? 'ذاتي' : 'مؤجر' });
    }
    if (filters.municipality) out.push({ label: "البلدية", value: filters.municipality.name || filters.municipality.municipality_name || '' });
    if (filters.mizan_selected) out.push({ label: "الميزان", value: filters.mizan_selected.name || '' });
    if (filters.sourceGeofence) out.push({ label: "نقطة التحميل", value: filters.sourceGeofence.name || '' });
    if (filters.unit_type_selected) out.push({ label: "نوع المركبة", value: filters.unit_type_selected.name || '' });
    if (filters.destinationGeofence) out.push({ label: "نقطة التفريغ", value: filters.destinationGeofence.name || '' });
    if (filters.transaction_status_selected?.length > 0) {
      out.push({ label: "حالة التدقيق", value: filters.transaction_status_selected.map(s => s.name).join('، ') });
    }
    if (Array.isArray(filters.groups_selected) && filters.groups_selected.length > 0) {
      out.push({ label: "المجاميع", value: filters.groups_selected.map(g => g?.name).filter(Boolean).join('، ') });
    }
    if (filters.creation_date_from || filters.creation_date_to) {
      const fromLabel = filters.creation_date_from ? moment(filters.creation_date_from).format('YYYY-MM-DD') : '—';
      const toLabel = filters.creation_date_to ? moment(filters.creation_date_to).format('YYYY-MM-DD') : '—';
      out.push({ label: "الفترة", value: `من ${fromLabel} إلى ${toLabel}` });
    }
    return out;
  };

  const exportFunction = () => {
    setFilters(prev => ({ ...prev, export_excel: true, export_pdf: false }));
    setReload(prev => !prev);
  };

  const exportPdfFunction = () => {
    setFilters(prev => ({ ...prev, export_pdf: true, export_excel: false }));
    setReload(prev => !prev);
  };

  const downloadExcelFile = async (fileName) => {
    const result = await transactionAPI?.downloadExcel({
      params: { ...headerObject, fileName }
    });
    if (result?.status && result?.data) {
      const _url = window.URL.createObjectURL(result.data);
      window.open(_url, "_blank")?.focus();
    } else {
      toast.error(t("NETWORK_MESSAGES.unknownError"));
    }
  };

  const downloadPdfFile = async (fileName) => {
    const result = await transactionAPI?.downloadPdf({
      params: { ...headerObject, fileName }
    });
    if (result?.status && result?.data) {
      // BE returns "<name>_0.pdf" for a single chunk or "<name>.zip" when chunks were zipped.
      const isZip = typeof fileName === 'string' && fileName.toLowerCase().endsWith('.zip');
      const ext = isZip ? '.zip' : '.pdf';
      const mime = isZip ? 'application/zip' : 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([result.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ملخص_حركات_المركبات' + ext);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } else {
      toast.error(t("NETWORK_MESSAGES.unknownError"));
    }
  };

  const handleRedirect = (unit_id, status_ids, status_selected, unit_name) => {
    const currentFilters = filtersRef.current;
    const dashboardFilters = {
      unit_id: unit_id || currentFilters.unit_id,
      unit: unit_name ? { id: unit_id, name: unit_name } : currentFilters.unit,
      unit_account_id: currentFilters.search_account_id,
      account: currentFilters.account,
      municipality_id: currentFilters.municipality_id,
      municipality: currentFilters.municipality,
      mizan_id: currentFilters.mizan_id,
      mizan_selected: currentFilters.mizan_selected,
      source_geofence_id: currentFilters.source_geofence_id,
      sourceGeofence: currentFilters.sourceGeofence,
      destination_geofence_id: currentFilters.destination_geofence_id,
      destinationGeofence: currentFilters.destinationGeofence,
      creationDateFrom: currentFilters.creationDateFrom,
      creationDateTo: currentFilters.creationDateTo,
      creation_date_from: currentFilters.creation_date_from,
      creation_date_to: currentFilters.creation_date_to,
      all_time: currentFilters.all_time,
      transaction_status_ids: status_ids,
      transaction_status_selected: status_selected
    };
    localStorage.setItem('dashboardFilters', JSON.stringify(dashboardFilters));
    window.open('/system/transactions_custom', '_blank', 'noopener,noreferrer');
  };

  const [gridColumns, setGridColumns] = useState([
    { field: 'unit_name', headerName: "اسم المركبة", flex: 1.5, headerAlign: 'center', align: 'center' },
    {
      field: 'total_transactions',
      headerName: "عدد الشحنات الكلي",
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box
          sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
          onClick={() => handleRedirect(params.row.unit_id, [], [], params.row.unit_name)}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: 'total_not_audited_transactions',
      headerName: "عدد الشحنات غير المدققة",
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box
          sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
          onClick={() => handleRedirect(params.row.unit_id, [1, 2], [
            { id: 1, name: "غير مدقق" },
            { id: 2, name: "قيد التدقيق" }
          ], params.row.unit_name)}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: 'total_audited_and_valid_transactions',
      headerName: "عدد الشحنات المعتمدة",
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box
          sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
          onClick={() => handleRedirect(params.row.unit_id, [4], [
            { id: 4, name: "معتمد" }
          ], params.row.unit_name)}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: 'total_audited_and_not_valid_transactions',
      headerName: "عدد الشحنات غير المعتمدة",
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box
          sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
          onClick={() => handleRedirect(params.row.unit_id, [3], [
            { id: 3, name: "غير معتمد" }
          ], params.row.unit_name)}
        >
          {params.value}
        </Box>
      )
    },
    { field: 'total_weights_of_transactions', headerName: "الوزن الكلي للشحنات (طن)", flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => params.value ? (params.value / 1000).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '0.000' },
    { field: 'total_weight_for_not_audited_transactions', headerName: "الوزن غير المدقق (طن)", flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => params.value ? (params.value / 1000).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '0.000' },
    { field: 'total_weight_for_audited_and_valid_transactions', headerName: "الوزن المعتمد (طن)", flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => params.value ? (params.value / 1000).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '0.000' },
    { field: 'total_weight_for_audited_and_not_valid_transactions', headerName: "الوزن غير المعتمد (طن)", flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => params.value ? (params.value / 1000).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '0.000' }
  ]);

  const handlePageChange = (newPage) => {
    setPageNumber(newPage + 1);
  };

  const searchFunction = () => {
    setReload(!reload);
    setPageNumber(1);
  };

  const applyFilterChange = (patch) => {
    setFilters(prev => ({ ...prev, ...patch }));
    setPageNumber(1);
    setReload(prev => !prev);
  };

  const activeFilterChips = (() => {
    const chips = [];

    if (filters.unit) {
      chips.push({
        key: 'unit',
        label: `المركبة: ${filters.unit.name || ''}`,
        onDelete: () => applyFilterChange({ unit_id: null, unit: null })
      });
    }
    if (filters.account) {
      chips.push({
        key: 'account',
        label: `العائدية: ${filters.account.name || ''}`,
        onDelete: () => applyFilterChange({ search_account_id: null, account: null })
      });
    }
    if (filters.is_governmental !== null && filters.is_governmental !== '') {
      const govLabel = String(filters.is_governmental) === '1' ? 'ذاتي' : 'مؤجر';
      chips.push({
        key: 'is_governmental',
        label: govLabel,
        onDelete: () => applyFilterChange({ is_governmental: null })
      });
    }
    if (filters.municipality) {
      chips.push({
        key: 'municipality',
        label: filters.municipality.name || filters.municipality.municipality_name || '',
        onDelete: () => applyFilterChange({ municipality_id: null, municipality: null })
      });
    }
    if (filters.mizan_selected) {
      chips.push({
        key: 'mizan',
        label: `الميزان: ${filters.mizan_selected.name || ''}`,
        onDelete: () => applyFilterChange({ mizan_id: null, mizan_selected: null })
      });
    }
    if (filters.sourceGeofence) {
      chips.push({
        key: 'source',
        label: `نقطة التحميل: ${filters.sourceGeofence.name || ''}`,
        onDelete: () => applyFilterChange({ source_geofence_id: null, sourceGeofence: null })
      });
    }
    if (filters.unit_type_selected) {
      chips.push({
        key: 'unit_type',
        label: `نوع المركبة: ${filters.unit_type_selected.name || ''}`,
        onDelete: () => applyFilterChange({ unit_type_id: null, unit_type_selected: null })
      });
    }
    if (filters.destinationGeofence) {
      chips.push({
        key: 'destination',
        label: `نقطة التفريغ: ${filters.destinationGeofence.name || ''}`,
        onDelete: () => applyFilterChange({ destination_geofence_id: null, destinationGeofence: null })
      });
    }
    if (filters.creation_date_from || filters.creation_date_to) {
      const fromLabel = filters.creation_date_from ? moment(filters.creation_date_from).format('YYYY-MM-DD') : '—';
      const toLabel = filters.creation_date_to ? moment(filters.creation_date_to).format('YYYY-MM-DD') : '—';
      const isDefaultMonth =
        filters.creation_date_from
        && filters.creation_date_to
        && moment(filters.creationDateFrom).isSame(moment().startOf('month'), 'day')
        && moment(filters.creationDateTo).isSame(moment(), 'day');
      chips.push({
        key: 'date_range',
        label: `من ${fromLabel} إلى ${toLabel}`,
        onDelete: isDefaultMonth ? null : () => applyFilterChange({
          creationDateFrom: moment().startOf('month').toDate(),
          creationDateTo: moment().endOf('day').toDate(),
          creation_date_from: moment().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
          creation_date_to: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
          all_time: 0
        })
      });
    }
    if (filters.transaction_status_selected?.length > 0) {
      filters.transaction_status_selected.forEach((status) => {
        chips.push({
          key: `status_${status.id}`,
          label: status.name,
          onDelete: () => {
            const newSelected = filters.transaction_status_selected.filter(s => s.id !== status.id);
            applyFilterChange({
              transaction_status_ids: newSelected.map(s => s.id),
              transaction_status_selected: newSelected
            });
          }
        });
      });
    }
    if (Array.isArray(filters.groups_selected) && filters.groups_selected.length > 0) {
      filters.groups_selected.forEach((g) => {
        chips.push({
          key: `group_${g?.id}`,
          label: g?.name || '',
          onDelete: () => {
            const remaining = filters.groups_selected.filter(x => x?.id !== g?.id);
            applyFilterChange({
              groups_selected: remaining,
              group_ids: remaining.map(x => x.id),
            });
          }
        });
      });
    }

    return chips;
  })();

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Title title="ملخص حركات المركبات" />

      {activeFilterChips.length > 0 && (
        <Box sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: '#f8f9fa',
          border: '1px solid #e9ecef',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5
        }}>
          <Typography sx={{ fontFamily: 'Cairo-Medium', color: '#495057', fontSize: '0.9rem' }}>
            تصفية النتائج حسب هذه القيم
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start' }}>
            {activeFilterChips.map(chip => (
              <Chip
                key={chip.key}
                label={chip.label}
                {...(chip.onDelete ? { onDelete: chip.onDelete } : {})}
                variant="outlined"
                sx={{
                  fontFamily: 'Cairo-Medium',
                  borderColor: '#dc3545',
                  color: '#dc3545',
                  '& .MuiChip-deleteIcon': { color: '#dc3545' }
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Summary Blocks */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2
      }}>
        <Box 
          onClick={() => {
            setFilters({ ...filters, transaction_status_ids: [], transaction_status_selected: [] });
            setReload(!reload);
            setPageNumber(1);
          }}
          sx={{ cursor: 'pointer', p: 2, borderRadius: 2, bgcolor: '#f8f9fa', border: '1px solid #e9ecef', display: 'flex', flexDirection: 'column', gap: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.02)', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)', bgcolor: '#f1f3f5' } }}>
          <Typography sx={{ fontFamily: 'Cairo-Medium', color: '#6c757d', fontSize: '0.85rem' }}>مجموع الشحنات</Typography>
          <Typography sx={{ fontFamily: 'Cairo-Bold', color: '#212529', fontSize: '1.4rem' }}>{totals.total_transactions.toLocaleString()}</Typography>
        </Box>
        <Box 
          onClick={() => {
            const status = transactionStatuses.filter(s => [1, 2].includes(s.id));
            setFilters({ ...filters, transaction_status_ids: [1, 2], transaction_status_selected: status });
            setReload(!reload);
            setPageNumber(1);
          }}
          sx={{ cursor: 'pointer', p: 2, borderRadius: 2, bgcolor: '#fff4e6', border: '1px solid #ffd8a8', display: 'flex', flexDirection: 'column', gap: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.02)', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)', bgcolor: '#fff9db' } }}>
          <Typography sx={{ fontFamily: 'Cairo-Medium', color: '#fd7e14', fontSize: '0.85rem' }}>مجموع الشحنات غير المدققة</Typography>
          <Typography sx={{ fontFamily: 'Cairo-Bold', color: '#d9480f', fontSize: '1.4rem' }}>{totals.total_not_audited.toLocaleString()}</Typography>
        </Box>
        <Box 
          onClick={() => {
            const status = transactionStatuses.filter(s => [4].includes(s.id));
            setFilters({ ...filters, transaction_status_ids: [4], transaction_status_selected: status });
            setReload(!reload);
            setPageNumber(1);
          }}
          sx={{ cursor: 'pointer', p: 2, borderRadius: 2, bgcolor: '#ebfbee', border: '1px solid #b2f2bb', display: 'flex', flexDirection: 'column', gap: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.02)', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)', bgcolor: '#d3f9d8' } }}>
          <Typography sx={{ fontFamily: 'Cairo-Medium', color: '#40c057', fontSize: '0.85rem' }}>مجموع الشحنات المعتمدة</Typography>
          <Typography sx={{ fontFamily: 'Cairo-Bold', color: '#2b8a3e', fontSize: '1.4rem' }}>{totals.total_audited_valid.toLocaleString()}</Typography>
        </Box>
        <Box 
          onClick={() => {
            const status = transactionStatuses.filter(s => [3].includes(s.id));
            setFilters({ ...filters, transaction_status_ids: [3], transaction_status_selected: status });
            setReload(!reload);
            setPageNumber(1);
          }}
          sx={{ cursor: 'pointer', p: 2, borderRadius: 2, bgcolor: '#fff5f5', border: '1px solid #ffc9c9', display: 'flex', flexDirection: 'column', gap: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.02)', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)', bgcolor: '#ffe3e3' } }}>
          <Typography sx={{ fontFamily: 'Cairo-Medium', color: '#fa5252', fontSize: '0.85rem' }}>مجموع الشحنات غير المعتمدة</Typography>
          <Typography sx={{ fontFamily: 'Cairo-Bold', color: '#c92a2a', fontSize: '1.4rem' }}>{totals.total_audited_invalid.toLocaleString()}</Typography>
        </Box>

        <Box 
          onClick={() => {
            setFilters({ ...filters, transaction_status_ids: [], transaction_status_selected: [] });
            setReload(!reload);
            setPageNumber(1);
          }}
          sx={{ cursor: 'pointer', p: 2, borderRadius: 2, bgcolor: '#f1f3f5', border: '1px solid #dee2e6', display: 'flex', flexDirection: 'column', gap: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.02)', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)', bgcolor: '#e9ecef' } }}>
          <Typography sx={{ fontFamily: 'Cairo-Medium', color: '#495057', fontSize: '0.85rem' }}>مجموع الوزن الكلي (طن)</Typography>
          <Typography sx={{ fontFamily: 'Cairo-Bold', color: '#212529', fontSize: '1.4rem' }}>{(totals.total_weight / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
        </Box>
        <Box 
          onClick={() => {
            const status = transactionStatuses.filter(s => [1, 2].includes(s.id));
            setFilters({ ...filters, transaction_status_ids: [1, 2], transaction_status_selected: status });
            setReload(!reload);
            setPageNumber(1);
          }}
          sx={{ cursor: 'pointer', p: 2, borderRadius: 2, bgcolor: '#fff9db', border: '1px solid #ffec99', display: 'flex', flexDirection: 'column', gap: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.02)', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)', bgcolor: '#fff3bf' } }}>
          <Typography sx={{ fontFamily: 'Cairo-Medium', color: '#f08c00', fontSize: '0.85rem' }}>مجموع الوزن غير المدقق (طن)</Typography>
          <Typography sx={{ fontFamily: 'Cairo-Bold', color: '#e67700', fontSize: '1.4rem' }}>{(totals.total_weight_not_audited / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
        </Box>
        <Box 
          onClick={() => {
            const status = transactionStatuses.filter(s => [4].includes(s.id));
            setFilters({ ...filters, transaction_status_ids: [4], transaction_status_selected: status });
            setReload(!reload);
            setPageNumber(1);
          }}
          sx={{ cursor: 'pointer', p: 2, borderRadius: 2, bgcolor: '#e3fafc', border: '1px solid #99e9f2', display: 'flex', flexDirection: 'column', gap: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.02)', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)', bgcolor: '#c5f6fa' } }}>
          <Typography sx={{ fontFamily: 'Cairo-Medium', color: '#15aabf', fontSize: '0.85rem' }}>مجموع الوزن المعتمد (طن)</Typography>
          <Typography sx={{ fontFamily: 'Cairo-Bold', color: '#0b7285', fontSize: '1.4rem' }}>{(totals.total_weight_valid / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
        </Box>
        <Box 
          onClick={() => {
            const status = transactionStatuses.filter(s => [3].includes(s.id));
            setFilters({ ...filters, transaction_status_ids: [3], transaction_status_selected: status });
            setReload(!reload);
            setPageNumber(1);
          }}
          sx={{ cursor: 'pointer', p: 2, borderRadius: 2, bgcolor: '#f3f0ff', border: '1px solid #d0bfff', display: 'flex', flexDirection: 'column', gap: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.02)', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)', bgcolor: '#e5dbff' } }}>
          <Typography sx={{ fontFamily: 'Cairo-Medium', color: '#7950f2', fontSize: '0.85rem' }}>مجموع الوزن غير المعتمد (طن)</Typography>
          <Typography sx={{ fontFamily: 'Cairo-Bold', color: '#5f3dc4', fontSize: '1.4rem' }}>{(totals.total_weight_invalid / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
        </Box>
      </Box>

      <Grid
        rows={rows}
        columns={gridColumns}
        setColumns={setGridColumns}
        hasColumnsHidShow={true}
        showAllColumns={true}
        useCookies={true}
        preferenceKey="report_unit_transaction_summary"
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        loading={loading}
        rowsTotal={rowsTotal}
        pageCount={pageCount}
        handlePageChange={handlePageChange}
        paginationMode="server"
        fixHeight={'calc(100vh - 300px)'}
        searchFunction={searchFunction}
        clearFunction={clearFilters}
        hasExport={false}
        exportFunction={exportFunction}
        multipleExports={[
          { icon: <SiMicrosoftexcel color="green" size={"25"} />, name: t('TRANSACTIONS.exportExcel') || 'تصدير Excel', key: 'unit_summary_excel', onClick: () => { exportFunction() } },
          { icon: <BsFilePdfFill color="red" size={"25"} />, name: t('TRANSACTIONS.exportPDF') || 'تصدير PDF', key: 'unit_summary_pdf', onClick: () => { exportPdfFunction() } }
        ]}
        filterChilds={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
             <Gate name="unit"><Autocomplete
              sx={{ width: '100%' }}
              options={units}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingUnits}
              onInputChange={(event, newInputValue) => handleSearchTermUnitChange(newInputValue)}
              onChange={(event, newValue) => setFilters({ ...filters, unit_id: newValue?.id || newValue?.unit_id || newValue?.WialonId, unit: newValue })}
              value={filters.unit}
              renderInput={(params) => <TextField {...params} label={t("UNITS.name")} size="small" variant="standard" />}
            /></Gate>
            <Gate name="account"><Autocomplete
              sx={{ width: '100%' }}
              options={accounts}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingAccounts}
              onInputChange={(event, newInputValue) => handleSearchTermAccountChange(newInputValue)}
              onChange={(event, newValue) => setFilters({ ...filters, search_account_id: newValue?.id || newValue?.account_id, account: newValue })}
              value={filters.account}
              renderInput={(params) => <TextField {...params} label="العائدية او الحساب" size="small" variant="standard" sx={{ '& .MuiInputLabel-root, & .MuiInputBase-input': { fontFamily: 'Cairo-Medium' } }} />}
            /></Gate>
            <Gate name="is_governmental"><TextField
              select
              fullWidth
              variant="standard"
              size="small"
              label={"نوع الجهد"}
              value={filters.is_governmental ?? ""}
              onChange={(e) => setFilters({ ...filters, is_governmental: e.target.value === "" ? null : e.target.value })}
              sx={{
                width: '100%',
                '& .MuiInputLabel-root': { fontFamily: 'Cairo-Medium' },
                '& .MuiInputBase-input': { fontFamily: 'Cairo-Medium' }
              }}
              SelectProps={{
                native: false,
                MenuProps: {
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        fontFamily: 'Cairo-Medium',
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="" sx={{ fontFamily: 'Cairo-Medium' }}>{t('GLOBAL.all') || "الكل"}</MenuItem>
              <MenuItem value="1" sx={{ fontFamily: 'Cairo-Medium' }}>{"ذاتي"}</MenuItem>
              <MenuItem value="0" sx={{ fontFamily: 'Cairo-Medium' }}>{"مؤجر"}</MenuItem>
            </TextField></Gate>
            <Gate name="municipality"><Autocomplete
              sx={{ width: '100%' }}
              options={municipalities}
              loading={loadingMunicipalities}
              onChange={(event, newValue) => setFilters({ ...filters, municipality_id: newValue?.id || newValue?.MunicipalityId, municipality: newValue })}
              value={filters.municipality}
              getOptionLabel={(option) => option?.name || option?.municipality_name || ""}
              renderInput={(params) => <TextField {...params} label={t("GLOBAL.municipalities")} size="small" variant="standard" sx={{ '& .MuiInputLabel-root, & .MuiInputBase-input': { fontFamily: 'Cairo-Medium' } }} />}
            /></Gate>
            <Gate name="mizan_selected"><Autocomplete
              sx={{ width: '100%' }}
              options={mizans}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingMizans}
              onChange={(event, newValue) => setFilters({ ...filters, mizan_id: newValue?.id || newValue?.MizanId || null, mizan_selected: newValue })}
              value={filters.mizan_selected}
              renderInput={(params) => <TextField {...params} label={t("MIZAN_UNITS.mizan") || "الميزان"} size="small" variant="standard" sx={{ '& .MuiInputLabel-root, & .MuiInputBase-input': { fontFamily: 'Cairo-Medium' } }} />}
            /></Gate>
            <Gate name="sourceGeofence"><Autocomplete
              sx={{ width: '100%' }}
              options={sourceGeofences}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingSourceGeofences}
              onInputChange={(event, newInputValue) => handleSearchTermSourceGeofenceChange(newInputValue)}
              onChange={(event, newValue) => setFilters({ ...filters, source_geofence_id: newValue?.id, sourceGeofence: newValue })}
              value={filters.sourceGeofence}
              renderInput={(params) => <TextField {...params} label={t("TRANSACTIONS.source_geofence")} size="small" variant="standard" sx={{ '& .MuiInputLabel-root, & .MuiInputBase-input': { fontFamily: 'Cairo-Medium' } }} />}
            /></Gate>
            <Gate name="unit_type_selected"><Autocomplete
              sx={{ width: '100%' }}
              options={unitTypes}
              getOptionLabel={(option) => option && option.name ? option.name : ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              loading={loadingUnitTypes}
              onChange={(event, newValue) => setFilters({ ...filters, unit_type_id: newValue?.id || null, unit_type_selected: newValue })}
              value={filters.unit_type_selected}
              renderInput={(params) => <TextField {...params} label={t("MIZAN_UNITS.unit_type") || "نوع المركبة"} size="small" variant="standard" sx={{ '& .MuiInputLabel-root, & .MuiInputBase-input': { fontFamily: 'Cairo-Medium' } }} />}
            /></Gate>
            <Gate name="destinationGeofence"><Autocomplete
              sx={{ width: '100%' }}
              options={destinationGeofences}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingDestinationGeofences}
              onInputChange={(event, newInputValue) => handleSearchTermDestinationGeofenceChange(newInputValue)}
              onChange={(event, newValue) => setFilters({ ...filters, destination_geofence_id: newValue?.id, destinationGeofence: newValue })}
              value={filters.destinationGeofence}
              renderInput={(params) => <TextField {...params} label={t("TRANSACTIONS.destination_geofence")} size="small" variant="standard" sx={{ '& .MuiInputLabel-root, & .MuiInputBase-input': { fontFamily: 'Cairo-Medium' } }} />}
            /></Gate>
            <Gate name="transaction_status_selected"><Autocomplete
              multiple
              sx={{ width: '100%' }}
              options={transactionStatuses}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingTransactionStatuses}
              onChange={(event, newValue) => {
                setFilters({
                  ...filters,
                  transaction_status_ids: newValue.map(item => item.id),
                  transaction_status_selected: newValue
                })
              }}
              value={filters.transaction_status_selected}
              renderInput={(params) => <TextField {...params} label="حالة التدقيق" size="small" variant="standard" sx={{ '& .MuiInputLabel-root, & .MuiInputBase-input': { fontFamily: 'Cairo-Medium' } }} />}
            /></Gate>
            <Gate name="groups_selected"><Autocomplete
              multiple
              sx={{ width: '100%' }}
              options={groups}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingGroups}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onChange={(event, newValue) => {
                setFilters({
                  ...filters,
                  group_ids: newValue.map(item => item.id),
                  groups_selected: newValue
                })
              }}
              value={filters.groups_selected}
              renderInput={(params) => <TextField {...params} label="المجاميع" size="small" variant="standard" sx={{ '& .MuiInputLabel-root, & .MuiInputBase-input': { fontFamily: 'Cairo-Medium' } }} />}
            /></Gate>
            <Gate name="creationDate"><CustomDateRange
              title={t('TRANSACTIONS.created_at')}
              showTitle={true}
              showAllTime={false}
              dateRange={filters}
              setDateRange={(value) => {
                setFilters({
                  ...filters,
                  ...value
                })
              }}
            /></Gate>
          </Box>
        }
      />
    </Box>
  );
}
